import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Zap, Plus, Trash2, X, Power, PowerOff, Clock, ArrowRight, Mail, MessageSquare, Tag, Activity } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_TYPES = [
  { id: 'new_lead', name: 'New Lead Created', desc: 'Fires when a new lead is submitted from any form' },
  { id: 'stage_change', name: 'Stage Changed', desc: 'Fires when a lead moves to a specific pipeline stage' },
  { id: 'tag_added', name: 'Tag Added', desc: 'Fires when a specific tag is added to a lead' },
];

const ACTION_TYPES = [
  { id: 'send_email', name: 'Send Email', desc: 'Send an email via SendGrid (MOCKED)', icon: Mail },
  { id: 'send_sms', name: 'Send SMS', desc: 'Send SMS via Twilio (MOCKED)', icon: MessageSquare },
  { id: 'add_tag', name: 'Add Tag', desc: 'Auto-tag the lead', icon: Tag },
  { id: 'move_stage', name: 'Move Stage', desc: 'Auto-move lead to another stage', icon: ArrowRight },
];

const STAGES = [
  'new_lead', 'contacted', 'qualified', 'appointment_set', 'consultation',
  'under_contract', 'clear_to_close', 'closed', 'follow_up', 'lost'
];

const STAGE_NAMES = {
  new_lead: 'New Lead', contacted: 'Contacted', qualified: 'Qualified',
  appointment_set: 'Appointment Set', consultation: 'Consultation',
  under_contract: 'Under Contract', clear_to_close: 'Clear to Close',
  closed: 'Closed', follow_up: 'Follow-Up', lost: 'Lost'
};

const TAGS = ['buyer', 'seller', 'investor', 'landlord', 'financing', 'notary', 'agent_recruit'];
const SOURCES = ['home', 'buyer', 'seller', 'investor', 'contact', 'open_house', 'notary', 'financing', 'popup'];

export const AdminAutomations = () => {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '',
    trigger_type: 'new_lead',
    trigger_config: {},
    action_type: 'send_email',
    action_config: {},
    is_active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, logsRes] = await Promise.all([
        axios.get(`${API}/automations`),
        axios.get(`${API}/automations/logs/recent`)
      ]);
      setRules(rulesRes.data);
      setLogs(logsRes.data);
    } catch (error) { console.error('Automations fetch error:', error); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/automations`, form);
      toast.success('Automation created');
      setShowCreate(false);
      setForm({ name: '', trigger_type: 'new_lead', trigger_config: {}, action_type: 'send_email', action_config: {}, is_active: true });
      fetchData();
    } catch { toast.error('Failed to create'); }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API}/automations/${rule.id}`, { is_active: !rule.is_active });
      toast.success(rule.is_active ? 'Automation disabled' : 'Automation enabled');
      fetchData();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this automation?')) return;
    try {
      await axios.delete(`${API}/automations/${id}`);
      toast.success('Automation deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-automations">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Automations
            </h1>
            <p className="text-[#808080] mt-1">Set up automated actions when leads are created or move stages</p>
            <p className="text-yellow-500/80 text-xs mt-1">Email (SendGrid) and SMS (Twilio) actions are currently MOCKED - logs only</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="btn-gold" data-testid="create-automation-btn">
            <Plus className="w-4 h-4 mr-2" /> New Automation
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : (
          <>
            {/* Rules List */}
            <div className="space-y-3 mb-8" data-testid="automation-rules-list">
              {rules.length === 0 ? (
                <div className="dashboard-card text-center py-12">
                  <Zap className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-3" />
                  <p className="text-[#808080]">No automations yet. Create one to get started.</p>
                </div>
              ) : (
                rules.map(rule => {
                  const triggerInfo = TRIGGER_TYPES.find(t => t.id === rule.trigger_type);
                  const actionInfo = ACTION_TYPES.find(a => a.id === rule.action_type);
                  const ActionIcon = actionInfo?.icon || Zap;
                  return (
                    <div key={rule.id} className={`dashboard-card flex items-center justify-between ${!rule.is_active ? 'opacity-50' : ''}`} data-testid={`automation-rule-${rule.id}`}>
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${rule.is_active ? 'bg-[#D4AF37]/10' : 'bg-[#333]'}`}>
                          <Zap className={`w-5 h-5 ${rule.is_active ? 'text-[#D4AF37]' : 'text-[#808080]'}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-[#F5F5F0] font-medium text-sm">{rule.name}</h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-[#808080]">
                            <span className="bg-[#1A1A1A] px-2 py-0.5 rounded">{triggerInfo?.name || rule.trigger_type}</span>
                            <ArrowRight className="w-3 h-3 text-[#D4AF37]" />
                            <span className="bg-[#1A1A1A] px-2 py-0.5 rounded flex items-center gap-1">
                              <ActionIcon className="w-3 h-3" /> {actionInfo?.name || rule.action_type}
                            </span>
                          </div>
                          {rule.trigger_config && Object.keys(rule.trigger_config).length > 0 && (
                            <p className="text-[10px] text-[#808080] mt-1">
                              Config: {JSON.stringify(rule.trigger_config)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <p className="text-xs text-[#808080]">{rule.execution_count || 0} runs</p>
                        </div>
                        <button
                          onClick={() => handleToggle(rule)}
                          className={`p-2 rounded transition-colors ${rule.is_active ? 'text-green-400 hover:bg-green-400/10' : 'text-[#808080] hover:bg-[#333]'}`}
                          data-testid={`toggle-automation-${rule.id}`}
                        >
                          {rule.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDelete(rule.id)} className="p-2 text-red-400 hover:bg-red-400/10 rounded" data-testid={`delete-automation-${rule.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Recent Logs */}
            {logs.length > 0 && (
              <div className="dashboard-card" data-testid="automation-logs">
                <h3 className="text-lg text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>Recent Execution Log</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {logs.map(log => (
                    <div key={log.id} className="flex items-center gap-3 p-2 bg-[#1A1A1A] rounded text-xs">
                      <Activity className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-[#F5F5F0]">{log.rule_name}</span>
                      <span className="text-[#808080]">on</span>
                      <span className="text-[#D4AF37]">{log.lead_name}</span>
                      <span className="ml-auto text-[#808080]">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="create-automation-modal">
            <div className="bg-[#111111] p-6 rounded-xl max-w-lg w-full mx-4 border border-[#D4AF37]/20 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>New Automation</h3>
                <button onClick={() => setShowCreate(false)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                {/* Name */}
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-2 block">Name</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Welcome email for new buyers" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="automation-name-input" />
                </div>

                {/* Trigger */}
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-2 block">When (Trigger)</label>
                  <div className="space-y-2">
                    {TRIGGER_TYPES.map(t => (
                      <button key={t.id} onClick={() => setForm({...form, trigger_type: t.id, trigger_config: {}})}
                        className={`w-full p-3 rounded-lg text-left text-sm border transition-colors ${form.trigger_type === t.id ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-[#F5F5F0]' : 'border-[#333] bg-[#1A1A1A] text-[#808080] hover:border-[#D4AF37]/30'}`}
                        data-testid={`trigger-${t.id}`}
                      >
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs mt-0.5 opacity-70">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                  {/* Trigger Config */}
                  {form.trigger_type === 'new_lead' && (
                    <div className="mt-3">
                      <label className="text-[#808080] text-xs mb-1 block">Filter by source (optional)</label>
                      <select value={form.trigger_config.source || ''} onChange={e => setForm({...form, trigger_config: {...form.trigger_config, source: e.target.value || undefined}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="trigger-source-select">
                        <option value="">Any source</option>
                        {SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                  )}
                  {form.trigger_type === 'stage_change' && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[#808080] text-xs mb-1 block">From stage</label>
                        <select value={form.trigger_config.from_stage || ''} onChange={e => setForm({...form, trigger_config: {...form.trigger_config, from_stage: e.target.value || undefined}})}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="trigger-from-stage">
                          <option value="">Any stage</option>
                          {STAGES.map(s => <option key={s} value={s}>{STAGE_NAMES[s]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[#808080] text-xs mb-1 block">To stage</label>
                        <select value={form.trigger_config.to_stage || ''} onChange={e => setForm({...form, trigger_config: {...form.trigger_config, to_stage: e.target.value || undefined}})}
                          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="trigger-to-stage">
                          <option value="">Any stage</option>
                          {STAGES.map(s => <option key={s} value={s}>{STAGE_NAMES[s]}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {form.trigger_type === 'tag_added' && (
                    <div className="mt-3">
                      <label className="text-[#808080] text-xs mb-1 block">When tag is added</label>
                      <select value={form.trigger_config.tag || ''} onChange={e => setForm({...form, trigger_config: {...form.trigger_config, tag: e.target.value || undefined}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="trigger-tag-select">
                        <option value="">Any tag</option>
                        {TAGS.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Action */}
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-2 block">Then (Action)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ACTION_TYPES.map(a => (
                      <button key={a.id} onClick={() => setForm({...form, action_type: a.id, action_config: {}})}
                        className={`p-3 rounded-lg text-left text-sm border transition-colors ${form.action_type === a.id ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-[#F5F5F0]' : 'border-[#333] bg-[#1A1A1A] text-[#808080] hover:border-[#D4AF37]/30'}`}
                        data-testid={`action-${a.id}`}
                      >
                        <a.icon className="w-4 h-4 mb-1" />
                        <p className="font-medium text-xs">{a.name}</p>
                      </button>
                    ))}
                  </div>
                  {/* Action Config */}
                  {form.action_type === 'send_email' && (
                    <div className="mt-3 space-y-2">
                      <select value={form.action_config.to || 'lead'} onChange={e => setForm({...form, action_config: {...form.action_config, to: e.target.value}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="action-email-to">
                        <option value="lead">Send to Lead</option>
                        <option value="admin">Send to Admin</option>
                      </select>
                      <Input value={form.action_config.subject || ''} onChange={e => setForm({...form, action_config: {...form.action_config, subject: e.target.value}})}
                        placeholder="Email subject" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="action-email-subject" />
                      <Input value={form.action_config.body || ''} onChange={e => setForm({...form, action_config: {...form.action_config, body: e.target.value}})}
                        placeholder="Email body" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="action-email-body" />
                    </div>
                  )}
                  {form.action_type === 'send_sms' && (
                    <div className="mt-3 space-y-2">
                      <select value={form.action_config.to || 'lead'} onChange={e => setForm({...form, action_config: {...form.action_config, to: e.target.value}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="action-sms-to">
                        <option value="lead">Send to Lead</option>
                      </select>
                      <Input value={form.action_config.message || ''} onChange={e => setForm({...form, action_config: {...form.action_config, message: e.target.value}})}
                        placeholder="SMS message" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="action-sms-message" />
                    </div>
                  )}
                  {form.action_type === 'add_tag' && (
                    <div className="mt-3">
                      <select value={form.action_config.tag || ''} onChange={e => setForm({...form, action_config: {...form.action_config, tag: e.target.value}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="action-tag-select">
                        <option value="">Select tag</option>
                        {TAGS.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                  )}
                  {form.action_type === 'move_stage' && (
                    <div className="mt-3">
                      <select value={form.action_config.stage || ''} onChange={e => setForm({...form, action_config: {...form.action_config, stage: e.target.value}})}
                        className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-sm" data-testid="action-stage-select">
                        <option value="">Select stage</option>
                        {STAGES.map(s => <option key={s} value={s}>{STAGE_NAMES[s]}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                <Button onClick={handleCreate} className="btn-gold w-full" data-testid="save-automation-btn">
                  Create Automation
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
